/**
 * Responsibility: the controls that decide which cards get screened.
 *
 * Behind one button, like the collection's, so the board itself gets the
 * page. What is set shows as badges beside it - see `DiscoveryBadges` - so
 * a filter is never on without being visible.
 *
 * **Era is a filter now, not a tab.** Modern and vintage were two of six
 * tabs, which made the page about which list you were on rather than what
 * you were looking for. It is a property of a card, so it belongs with the
 * other properties.
 *
 * **Keywords narrow what came back, they do not search.** The board is a
 * screen over a population report, and the report is the whole universe of
 * Pokemon cards PSA has graded; typing a name here filters the rows the
 * screen produced. Finding a card by name anywhere in the market is what
 * the Search page is for, and saying so is better than a box that looks
 * like search and behaves differently.
 *
 * There is no category or sport control. The population report behind this
 * board is Pokemon only - see `betterpop_source` - so a sport menu would
 * be a control that can only ever return nothing. The universe badge says
 * what is being screened instead.
 */
import { useState } from 'react';
import Badge from '@mui/material/Badge';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import MenuItem from '@mui/material/MenuItem';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import ToggleButton from '@mui/material/ToggleButton';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import Typography from '@mui/material/Typography';
import ClearIcon from '@mui/icons-material/FilterAltOffOutlined';
import FilterIcon from '@mui/icons-material/FilterAltOutlined';
import type { CardEra, CardLanguage } from '../../api/client';
import type { DiscoveryFilters } from './discoveryFilters';
import { DEFAULT_FILTERS, activeCount } from './discoveryFilters';

const LANGUAGES: { value: CardLanguage; label: string }[] = [
  { value: 'EN', label: 'English' },
  { value: 'JP', label: 'Japanese' },
  { value: 'ANY', label: 'All languages' },
];

const FLOOR_HELP =
  'Minimum PSA 10 population - how much evidence a gem price needs. Below five, a PSA 10 price often belongs to a card no PSA 10 exists for.';

const LANGUAGE_HELP =
  'English and Japanese are separate markets: a Japanese run is larger and graded harder, so the two are not comparable on one board. All also reaches Korean and the European printings.';

const ERA_HELP =
  'Vintage is the WOTC era and its contemporaries; modern is everything since. They are screened apart because a modern population of 200 is thin and a vintage one is not.';

const KEYWORD_HELP =
  'Narrows the rows already on the board by name or set. It does not search the market - the Search page does that.';

type DiscoveryFiltersProps = {
  filters: DiscoveryFilters;
  onChange: (next: DiscoveryFilters) => void;
  offeredFloors: number[];
  busy: boolean;
};

export function DiscoveryFilterButton({
  filters,
  onChange,
  offeredFloors,
  busy,
}: DiscoveryFiltersProps) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  // Held apart from the applied floor so typing "1" on the way to "120"
  // does not re-screen the universe at every keystroke.
  const [draft, setDraft] = useState('');

  const set = <K extends keyof DiscoveryFilters>(key: K, value: DiscoveryFilters[K]) =>
    onChange({ ...filters, [key]: value });

  const active = activeCount(filters);
  const offered = offeredFloors.includes(filters.floor);

  const applyDraft = () => {
    const parsed = Number(draft);
    if (Number.isFinite(parsed) && parsed >= 0) set('floor', Math.floor(parsed));
  };

  return (
    <>
      <Badge badgeContent={active} color="primary">
        <Button
          size="small"
          variant={active === 0 ? 'outlined' : 'contained'}
          startIcon={<FilterIcon />}
          onClick={(event) => setAnchor(event.currentTarget)}
          disabled={busy}
        >
          Filters
        </Button>
      </Badge>

      <Popover
        open={anchor !== null}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        slotProps={{ paper: { sx: { p: 2, maxWidth: 560 } } }}
      >
        <Stack spacing={1.5}>
          <Typography variant="caption" color="text.secondary">
            {ERA_HELP}
          </Typography>

          <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap alignItems="center">
            <ToggleButtonGroup
              size="small"
              exclusive
              value={filters.era}
              onChange={(_event, next: CardEra | null) => next && set('era', next)}
            >
              <ToggleButton value="MODERN">Modern</ToggleButton>
              <ToggleButton value="VINTAGE">Vintage</ToggleButton>
            </ToggleButtonGroup>

            <TextField
              select
              size="small"
              label="Language"
              value={filters.language}
              onChange={(event) => set('language', event.target.value as CardLanguage)}
              sx={{ minWidth: 150 }}
              helperText={LANGUAGE_HELP}
            >
              {LANGUAGES.map((entry) => (
                <MenuItem key={entry.value} value={entry.value}>
                  {entry.label}
                </MenuItem>
              ))}
            </TextField>

          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            <Typography variant="caption" color="text.secondary" sx={{ minWidth: 92 }}>
              PSA 10 floor
            </Typography>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={offered ? filters.floor : null}
              onChange={(_event, next: number | null) => next !== null && set('floor', next)}
            >
              {offeredFloors.map((value) => (
                <ToggleButton key={value} value={value}>
                  {value}+
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <TextField
              size="small"
              label="Custom"
              value={draft}
              helperText={FLOOR_HELP}
              onChange={(event) => setDraft(event.target.value.replace(/[^0-9]/g, ''))}
              onBlur={applyDraft}
              onKeyDown={(event) => event.key === 'Enter' && applyDraft()}
              sx={{ width: 110 }}
              slotProps={{
                input: { endAdornment: <InputAdornment position="end">+</InputAdornment> },
              }}
            />
          </Stack>

          <TextField
            size="small"
            label="Keywords"
            placeholder="charizard, evolving skies"
            value={filters.keywords}
            onChange={(event) => set('keywords', event.target.value)}
            helperText={KEYWORD_HELP}
          />

          {active > 0 ? (
            <Stack direction="row">
              <Button
                size="small"
                startIcon={<ClearIcon />}
                onClick={() => onChange(DEFAULT_FILTERS)}
              >
                Reset
              </Button>
            </Stack>
          ) : null}
        </Stack>
      </Popover>
    </>
  );
}
